import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from './LanguageContext';
import toast from 'react-hot-toast';

export interface ErrorInfo {
  id: string;
  type: 'api' | 'network' | 'validation' | 'permission' | 'unknown';
  message: string;
  details?: any;
  timestamp: Date;
  retryable: boolean;
  retryAction?: () => void;
}

interface ErrorContextType {
  errors: ErrorInfo[];
  addError: (error: Omit<ErrorInfo, 'id' | 'timestamp'>) => string;
  removeError: (id: string) => void;
  clearErrors: () => void;
  handleApiError: (error: any, retryAction?: () => void) => string;
  handleNetworkError: (retryAction?: () => void) => string;
  handleValidationError: (message: string) => string;
  handlePermissionError: (retryAction?: () => void) => string;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

interface ErrorProviderProps {
  children: ReactNode;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
  const [errors, setErrors] = useState<ErrorInfo[]>([]);
  const { t } = useTranslation();
  const { isRTL } = useLanguage();

  const generateId = () => `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const addError = useCallback((error: Omit<ErrorInfo, 'id' | 'timestamp'>): string => {
    const id = generateId();
    const errorInfo: ErrorInfo = {
      ...error,
      id,
      timestamp: new Date()
    };

    setErrors(prev => [...prev, errorInfo]);

    // Show toast notification
    const toastMessage = getToastMessage(errorInfo, t);
    const toastOptions = {
      duration: errorInfo.type === 'network' ? 0 : 4000, // Network errors persist until dismissed
      icon: getErrorIcon(errorInfo.type),
      style: {
        direction: isRTL ? 'rtl' as const : 'ltr' as const,
        textAlign: isRTL ? 'right' as const : 'left' as const
      }
    };

    if (errorInfo.retryable && errorInfo.retryAction) {
      toast.error(
        <div>
          <div>{toastMessage}</div>
          <button
            onClick={errorInfo.retryAction}
            className="mt-2 text-sm underline hover:no-underline"
          >
            {t('errors.retry', 'Retry')}
          </button>
        </div>,
        toastOptions
      );
    } else {
      toast.error(toastMessage, toastOptions);
    }

    // Auto-remove non-critical errors after 30 seconds
    if (errorInfo.type !== 'network' && errorInfo.type !== 'permission') {
      setTimeout(() => {
        removeError(id);
      }, 30000);
    }

    return id;
  }, [t, isRTL]);

  const removeError = useCallback((id: string) => {
    setErrors(prev => prev.filter(error => error.id !== id));
  }, []);

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  const handleApiError = useCallback((error: any, retryAction?: () => void): string => {
    let message = t('errors.apiError', 'An API error occurred');
    let type: ErrorInfo['type'] = 'api';
    let retryable = false;

    if (error?.response?.status) {
      const status = error.response.status;
      
      switch (status) {
        case 400:
          message = t('errors.badRequest', 'Invalid request. Please check your input.');
          break;
        case 401:
          message = t('errors.unauthorized', 'You are not authorized. Please log in again.');
          break;
        case 403:
          message = t('errors.forbidden', 'You do not have permission to perform this action.');
          type = 'permission';
          break;
        case 404:
          message = t('errors.notFound', 'The requested resource was not found.');
          break;
        case 409:
          message = t('errors.conflict', 'A conflict occurred. Please refresh and try again.');
          retryable = true;
          break;
        case 422:
          message = error.response.data?.message || t('errors.validationError', 'Validation failed. Please check your input.');
          type = 'validation';
          break;
        case 429:
          message = t('errors.rateLimit', 'Too many requests. Please wait a moment and try again.');
          retryable = true;
          break;
        case 500:
          message = t('errors.serverError', 'Server error. Please try again later.');
          retryable = true;
          break;
        case 502:
        case 503:
        case 504:
          message = t('errors.serviceUnavailable', 'Service temporarily unavailable. Please try again later.');
          retryable = true;
          break;
        default:
          message = error.response.data?.message || t('errors.apiError', 'An API error occurred');
          retryable = status >= 500;
      }
    } else if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('Network Error')) {
      message = t('errors.networkError', 'Network connection error. Please check your internet connection.');
      type = 'network';
      retryable = true;
    } else if (error?.message?.includes('timeout')) {
      message = t('errors.timeout', 'Request timed out. Please try again.');
      retryable = true;
    }

    return addError({
      type,
      message,
      details: error,
      retryable,
      retryAction
    });
  }, [addError, t]);

  const handleNetworkError = useCallback((retryAction?: () => void): string => {
    return addError({
      type: 'network',
      message: t('errors.networkError', 'Network connection error. Please check your internet connection.'),
      retryable: true,
      retryAction
    });
  }, [addError, t]);

  const handleValidationError = useCallback((message: string): string => {
    return addError({
      type: 'validation',
      message,
      retryable: false
    });
  }, [addError]);

  const handlePermissionError = useCallback((retryAction?: () => void): string => {
    return addError({
      type: 'permission',
      message: t('errors.permissionDenied', 'You do not have permission to perform this action.'),
      retryable: true,
      retryAction
    });
  }, [addError, t]);

  const value: ErrorContextType = {
    errors,
    addError,
    removeError,
    clearErrors,
    handleApiError,
    handleNetworkError,
    handleValidationError,
    handlePermissionError
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
    </ErrorContext.Provider>
  );
};

// Helper functions
const getToastMessage = (error: ErrorInfo, t: any): string => {
  return error.message;
};

const getErrorIcon = (type: ErrorInfo['type']): string => {
  switch (type) {
    case 'network':
      return '📡';
    case 'validation':
      return '⚠️';
    case 'permission':
      return '🔒';
    case 'api':
      return '🚨';
    default:
      return '❌';
  }
};
